// LEGACY-V2-BRIDGE — TEMPORARIO. Saca de Rambox 2 a las cuentas que ya son de Rambox 3 y las manda a la consola
// ya logueadas. Se borra con este archivo y sus usos (buscá LEGACY-V2-BRIDGE) el día que se apague la v2, o
// junto con toda esta app. La otra mitad vive en el monorepo: apps/api/src/legacy-v2-bridge/README.md.

const isDev = process.env.NODE_ENV === 'development';

/**
 * El API de Rambox 3, ya con la versión: el API versiona por URI y sin `/v1` contesta 404.
 * `VUE_APP_v3ApiUrl` / `VUE_APP_consoleUrl` pisan los valores por defecto: sirven para probar desde dev contra
 * producción (`cross-env VUE_APP_v3ApiUrl=https://api.rambox.app/v1 ... npm run dev`) sin tocar el código.
 */
const V3_API_URL = process.env.VUE_APP_v3ApiUrl || (isDev ? 'http://localhost:3000/v1' : 'https://api.rambox.app/v1');
const CONSOLE_URL = process.env.VUE_APP_consoleUrl || (isDev ? 'http://localhost:4201' : 'https://console.rambox.app');
/** El login de Rambox 3 (apps/auth). `VUE_APP_authUrl` lo pisa, igual que las dos de arriba. */
const AUTH_URL = process.env.VUE_APP_authUrl || (isDev ? 'http://localhost:4200' : 'https://auth.rambox.app');

/** Si el API de v3 no contesta en este tiempo, se sigue en la v2: nunca dejamos a nadie colgado acá. */
const TIMEOUT_MS = 6000;

/**
 * Pregunta al API de Rambox 3 si la sesión recién iniciada pertenece a una cuenta (u organización) que ya es
 * v3. Si lo es, devuelve la URL de la consola a la que hay que mandarla, con el código de handoff (`#code=`)
 * para que entre sin volver a loguearse; si no, `null`.
 *
 * Cualquier falla (red, timeout, error del API) devuelve `null`: ante la duda la persona sigue en la v2, que es
 * lo que pasaba antes de este puente. Lo contrario dejaría afuera a usuarios v2 si el API de v3 se cae.
 */
export async function getV3ConsoleUrl(idToken: string, consolePath: string): Promise<string | null> {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
	try {
		const response = await fetch(`${V3_API_URL}/legacy-v2/destination`, {
			method: 'POST',
			// `X-Rambox-Legacy-App`: sólo para la telemetría del API (`legacy_bridge.sent_to_console`), para saber de qué
			// app v2 vino cada rebote. Es opcional: sin él el evento sale igual, con la app como desconocida.
			headers: { Authorization: `Bearer ${idToken}`, 'X-Rambox-Legacy-App': 'admin' },
			signal: controller.signal,
		});
		if (!response.ok) return null;
		const body: { environment?: string; code?: string | null } = await response.json();
		if (body.environment !== 'v3') return null;
		// Sin código (la consola rechazaría la cuenta: baneada, desactivada…) igual va a la consola: ahí el login
		// de v3 le explica qué pasa. Lo que no hace es quedarse en la v2.
		return `${CONSOLE_URL}${consolePath}${body.code ? `#code=${encodeURIComponent(body.code)}` : ''}`;
	} catch (error) {
		console.error('LEGACY_V2_BRIDGE', 'Could not ask Rambox 3 where this account belongs, staying on v2', error);
		return null;
	} finally {
		clearTimeout(timer);
	}
}

/**
 * Entra con el custom token que la consola de Rambox 3 pone en la URL (`?token=`) cuando una cuenta enterprise
 * que sigue en v2 llega a ella desde un link de campaña: en vez del normalizador, la consola la manda acá, ya
 * logueada, igual que a una cuenta personal la manda a billing v2. Es el mismo mecanismo con el que billing v2
 * recibe su token de auth-legacy.
 *
 * Devuelve `true` si había token y se inició sesión (el `onAuthStateChanged` de App.vue sigue desde ahí como con
 * cualquier login). Con un token inválido o vencido devuelve `false` y la persona ve el login de siempre. En los
 * dos casos el token se saca de la URL con replaceState, para que no quede en el historial ni se reintente al
 * recargar.
 */
export async function signInWithUrlToken(auth: {
	tenantId: string | null;
	signInWithCustomToken(token: string): Promise<unknown>;
}): Promise<boolean> {
	const url = new URL(window.location.href);
	const token = url.searchParams.get('token');
	if (!token) return false;
	url.searchParams.delete('token');
	window.history.replaceState(null, '', url.pathname + url.search + url.hash);
	try {
		// Un custom token de un tenant lleva el claim `tenant_id`, y el SDK exige que `auth.tenantId` coincida antes
		// de canjearlo: se lee del payload (base64url, sin verificar: el servidor lo verifica al canjearlo).
		const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))) as {
			tenant_id?: string;
		};
		auth.tenantId = payload.tenant_id || null;
		await auth.signInWithCustomToken(token);
		return true;
	} catch (error) {
		console.error('LEGACY_V2_BRIDGE', 'Could not sign in with the token from the console, showing the sign-in', error);
		return false;
	}
}

/**
 * URL del asistente de migración de la consola (`/migrate-schema`) para la sesión v2 actual, con un código de
 * handoff (`#code=`) para que entre sin volver a loguearse. Es lo mismo que hace apps/auth con las cuentas v2
 * que tienen que migrar (console-redirect.ts del monorepo): `POST /auth/console-handoffs` acepta el ID token
 * de la v2 porque los dos ambientes comparten el proyecto de Firebase.
 *
 * El código dura un minuto y sirve una vez, así que se pide en el momento del click y no antes. Si el API no
 * lo da (red, timeout, cuenta rechazada) se manda a la persona al login por empresa de Rambox 3
 * (`/enterprise/:organizationId` de apps/auth) con su organización ya cargada, y con `redirect_uri` al
 * asistente: al loguearse, auth la deja en la consola con un handoff propio. Es lo que haría la consola sola
 * (redirectToAuth de apps/console), pero sin pasar por el login de cuentas personales, que a un admin
 * enterprise le haría buscar "Sign in with your company ID". Nunca se queda sin poder migrar por esto.
 */
export async function getMigrationConsoleUrl(idToken: string, organizationId: string): Promise<string> {
	const url = `${CONSOLE_URL}/migrate-schema`;
	// Mismos parámetros que manda la consola cuando no hay sesión (apps/console/src/utils/auth-redirect.ts), más
	// `logout=true`: auth guarda su propia sesión y su login por empresa sigue con la que encuentre SIN mirar si
	// es de esta organización. Una sesión vieja de otra cuenta (la personal del admin, otra empresa) terminaría
	// en la consola migrando la cuenta equivocada. Así auth la cierra y pide entrar con la de esta empresa.
	const params = new URLSearchParams({
		redirect_uri: url,
		response_type: 'token',
		scope: 'organization email',
		logout: 'true',
	});
	const fallback = `${AUTH_URL}/enterprise/${encodeURIComponent(organizationId)}?${params.toString()}`;
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
	try {
		const response = await fetch(`${V3_API_URL}/auth/console-handoffs`, {
			method: 'POST',
			headers: { Authorization: `Bearer ${idToken}` },
			signal: controller.signal,
		});
		if (!response.ok) throw new Error(`HTTP ${response.status}`);
		const body: { code?: string } = await response.json();
		return body.code ? `${url}#code=${encodeURIComponent(body.code)}` : fallback;
	} catch (error) {
		console.error('LEGACY_V2_BRIDGE', 'Could not get a console handoff code, sending to the company sign-in', error);
		return fallback;
	} finally {
		clearTimeout(timer);
	}
}
